import {
  Box,
  DrawerCloseButton,
  DrawerContent,
  Flex,
  Icon,
  IconButton,
  InputGroup,
  InputLeftAddon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import { IconPen, IconTrashbin } from 'assets/icons';
import { ControllableDrawer } from 'components/Drawer';
import { ConfirmationModal } from 'components/Modal';
import ReactSelect from 'components/ReactSelect';
import SearchField from 'components/SearchField';
import Table, { IndeterminateCheckbox } from 'components/Table';
import AddCategory from 'containers/CompanySettings/TimeOffSetup/RightDrawer/CategoryForm';
import { initialCategoryFormData } from 'containers/CompanySettings/TimeOffSetup/RightDrawer/CategoryForm/schema';
import { accrualMethods } from 'containers/CompanySettings/TimeOffSetup/shared/options';
import { daysOfMonth, monthOptions, statusOptions, weekDays } from 'global/options';
import { useBulkDisableCategories } from 'hooks/timeOff/useBulkDisableCategories';
import { useBulkEnableCategories } from 'hooks/timeOff/useBulkEnableCategories';
import { IDeleteCategories, useDeleteCategory } from 'hooks/timeOff/useDeleteCategory';
import { useFetchPaginatedCategory, ICategorySearchQuery } from 'hooks/timeOff/useFetchPaginatedCategories';

import CategoriesTableFooter from './CategoriesTableFooter';

const initialTimeOffCategory: ICategorySearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
  active: '1',
};

const Categories: React.FC<unknown> = () => {
  const { t } = useTranslation(['timeOff', 'common']);

  const [dialogIds, setDialogIds] = useState<IDeleteCategories>({ ids: [] });
  const [searchQuery, setSearchQuery] = useState<ICategorySearchQuery>(initialTimeOffCategory);
  const [formData, setFormData] = useState<typeof initialCategoryFormData>(initialCategoryFormData);

  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
  const { isOpen: isEnableModalOpen, onOpen: onEnableModalOpen, onClose: onEnableModalClose } = useDisclosure();
  const { isOpen: isDisableModalOpen, onOpen: onDisableModalOpen, onClose: onDisableModalClose } = useDisclosure();
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();

  const { status: categoryListStatus, data: categoryListData, refetch } = useFetchPaginatedCategory(searchQuery);
  const { status: deleteStatus, mutateAsync: mutateDeleteAsync } = useDeleteCategory();
  const { status: enableCategoriesStatus, mutateAsync: mutateAsyncEnableCategories } = useBulkEnableCategories();
  const { status: disableCategoriesStatus, mutateAsync: mutateAsyncDisableCategories } = useBulkDisableCategories();

  const columns = useMemo(
    () => [
      {
        id: 'selection',
        width: 30,
        Header: ({ getToggleAllRowsSelectedProps }: any) => {
          return <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} colorScheme="secondary" />;
        },
        Cell: ({ row }: any) => {
          return <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} colorScheme="secondary" />;
        },
      },
      {
        Header: t('timeOff:categoryName'),
        accessor: 'name',
        Cell: (data: CellValue) => {
          return (
            <div>
              <Text fontWeight="semibold" color="primary.500" textTransform="capitalize">
                {data.row.original?.name}
              </Text>
            </div>
          );
        },
      },
      {
        Header: t('common:status'),
        accessor: 'active',
        width: 120,
        Cell: (data: CellValue) => {
          return data.value ? (
            <Text color="success.500" fontWeight="semibold">
              {t('common:active')}
            </Text>
          ) : (
            <Text color="secondary.700" fontWeight="semibold">
              {t('common:inactive')}
            </Text>
          );
        },
      },
      {
        Header: 'Type',
        width: 120,
        accessor: 'is_paid',
        Cell: (data: CellValue) => <Text color="gray.500">{data.value ? 'Paid' : 'Unpaid'}</Text>,
      },
      {
        Header: 'Applicable For',
        accessor: 'users',
        width: 200,
        Cell: (data: CellValue) => {
          const users = data.value || [];
          return (
            <Menu isLazy={true} lazyBehavior="unmount">
              <MenuButton textAlign="left">
                <Tag fontWeight="semibold" color="primary.500">
                  {Array.isArray(users) && users.length
                    ? users.length === 1
                      ? `${data.value[0]?.firstName} ${data.value[0]?.lastName}`
                      : `${data.value[0]?.firstName} ${data.value[0]?.lastName} and ${data.value.length - 1} others`
                    : 'All'}
                </Tag>
              </MenuButton>
              {users.length > 0 ? (
                <MenuList>
                  {users.map((item: any, index: number) => (
                    <MenuItem color="primary.500" fontWeight="600" key={index}>
                      {item?.firstName} {item?.lastName}
                    </MenuItem>
                  ))}
                </MenuList>
              ) : null}
            </Menu>
          );
        },
      },

      {
        Header: t('common:description'),
        accessor: 'description',
        Cell: (data: CellValue) => {
          return <Text>{data.row.original?.description}</Text>;
        },
      },
      {
        Header: '',
        accessor: 'action',
        Cell: (data: CellValue) => {
          return (
            <div>
              <Tooltip label={t('timeOff:updateCategory')} hasArrow bg="black">
                <IconButton
                  aria-label="Edit"
                  bg="rgba(38, 103, 201, 0.1)"
                  icon={<Icon as={IconPen} />}
                  size="sm"
                  mr="3"
                  _hover={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                  onClick={() => handleEditIconClick(data.row.original)}
                />
              </Tooltip>
              <Tooltip label={t('timeOff:deleteCategory')} hasArrow bg="black">
                <IconButton
                  aria-label="Delete"
                  bg="rgba(255, 83, 83, 0.1)"
                  icon={<Icon as={IconTrashbin} />}
                  size="sm"
                  _hover={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                  onClick={() => handleSingleDelete(data.row.original.id)}
                />
              </Tooltip>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const parseCronExpressionFields = (accrueType: string, cronExpression: string) => {
    const result: any = {
      accrue_type: accrualMethods.find(method => method.value === 'Daily'),
      accrue_hourly: '',
      accrue_week_day: '',
      accrue_month_day_one: '',
      accrue_month_day_two: '',
      accrue_year_day: '',
      accrue_year_month_one: '',
      accrue_year_month_two: '',
      accrue_year_month_three: '',
      accrue_year_month_four: '',
    };

    if (accrueType === 'Hourly') {
      result.accrue_type = accrualMethods.find(method => method.value === 'Hourly');
      result.accrue_hourly = cronExpression || '0';
      return result;
    }

    if (!cronExpression) return result;

    const parseDay = (dayStr: string) => {
      return dayStr === 'L' ? 'L' : Number(dayStr);
    };

    const [, , dayOfMonth, month, dayOfWeek] = cronExpression.split(' ');

    switch (accrueType) {
      case 'Daily':
        result.accrue_type = accrualMethods.find(method => method.value === 'Daily');
        break;

      case 'Weekly':
        result.accrue_type = accrualMethods.find(method => method.value === 'Weekly');
        result.accrue_week_day = weekDays.find(day => day.value === Number(dayOfWeek));
        break;

      case 'Bi-Monthly':
        result.accrue_type = accrualMethods.find(method => method.value === 'Bi-Monthly');
        const days = dayOfMonth.split(',');
        result.accrue_month_day_one = daysOfMonth.find(day => day.value === parseDay(days[0]));
        result.accrue_month_day_two = daysOfMonth.find(day => day.value === parseDay(days[1]));
        break;

      case 'Monthly':
        result.accrue_type = accrualMethods.find(method => method.value === 'Monthly');
        result.accrue_month_day_one = daysOfMonth.find(day => day.value === parseDay(dayOfMonth));
        break;

      case 'Quarterly':
        result.accrue_type = accrualMethods.find(method => method.value === 'Quarterly');
        result.accrue_year_day = daysOfMonth.find(day => day.value === parseDay(dayOfMonth));
        const monthsQuarterly = month.split(',');
        result.accrue_year_month_one = monthOptions.find(m => m.value === Number(monthsQuarterly[0]));
        result.accrue_year_month_two = monthOptions.find(m => m.value === Number(monthsQuarterly[1]));
        result.accrue_year_month_three = monthOptions.find(m => m.value === Number(monthsQuarterly[2]));
        result.accrue_year_month_four = monthOptions.find(m => m.value === Number(monthsQuarterly[3]));
        break;

      case 'Semi-Annually':
        result.accrue_type = accrualMethods.find(method => method.value === 'Semi-Annually');
        result.accrue_year_day = daysOfMonth.find(day => day.value === parseDay(dayOfMonth));
        const monthsTwiceAYear = month.split(',');
        result.accrue_year_month_one = monthOptions.find(m => m.value === Number(monthsTwiceAYear[0]));
        result.accrue_year_month_two = monthOptions.find(m => m.value === Number(monthsTwiceAYear[1]));
        break;

      case 'Annually':
        result.accrue_type = accrualMethods.find(method => method.value === 'Annually');
        result.accrue_year_day = daysOfMonth.find(day => day.value === parseDay(dayOfMonth));
        result.accrue_year_month_one = monthOptions.find(m => m.value === Number(month));
        break;

      case 'Anniversary':
        result.accrue_type = accrualMethods.find(method => method.value === 'Anniversary');
        break;

      default:
        break;
    }

    return result;
  };

  const handleEditIconClick = (categoryData: any) => {
    const cronExpressionFields = parseCronExpressionFields(categoryData?.accrue_type, categoryData?.accrue_period);

    const newFormData: typeof initialCategoryFormData = {
      ...initialCategoryFormData,
      ...cronExpressionFields,
      id: categoryData?.id || '',
      name: categoryData?.name || '',
      description: categoryData?.description || '',
      is_paid: categoryData?.is_paid ? 'true' : 'false',
      in_hours: categoryData?.in_hours ? 'true' : 'false',
      color: categoryData?.color || '',
      active: categoryData?.active ? true : false,
      users: categoryData?.users || [],
      accrue_period: categoryData?.accrue_period || '',
      accrue_per_period: categoryData?.accrue_per_period || '0',
      min_request_duration: categoryData?.min_request_duration || '',
      max_request_duration: categoryData?.max_request_duration || '',

      set_waiting_period: categoryData?.set_waiting_period ? '1' : '0',
      accrue_start: categoryData?.accrue_start ? new Date(categoryData?.accrue_start) : null,
      set_max_balance: categoryData?.set_max_balance ? '1' : '0',
      max_balance: categoryData?.max_balance || '',

      reset_balance_yearly: categoryData?.reset_balance_yearly ? '1' : '0',
      carryover_expiration_type: categoryData?.reset_balance_yearly
        ? null
        : categoryData?.max_carryover_balance
          ? { label: 'Up to...', value: 'up-to' }
          : { label: 'Unlimited', value: 'unlimited' },
      max_carryover_balance: categoryData?.max_carryover_balance || '',

      allow_negative_balance: categoryData?.allow_negative_balance ? true : false,
      initial_balance: categoryData?.initial_balance || '0',
    };

    setFormData(newFormData);
    onDrawerOpen();
  };

  const handleDrawerClose = () => {
    setFormData(initialCategoryFormData);
    onDrawerClose();
  };

  //enable categories handler

  const handleBulkEnable = (selectRows: any) => {
    const ids = selectRows.filter((row: any) => !row.original.active).map((row: any) => row.original.id);
    setDialogIds({ ids: ids });
    onEnableModalOpen();
  };

  const enableCategories = () => {
    mutateAsyncEnableCategories(dialogIds).then(() => {
      refetch();
      onEnableModalClose();
      setDialogIds({ ids: [] });
    });
  };

  //disable categories handler
  const handleBulkDisable = (selectRows: any) => {
    const ids = selectRows.filter((row: any) => row.original.active).map((row: any) => row.original.id);
    setDialogIds({ ids: ids });
    onDisableModalOpen();
  };

  const disableCategories = () => {
    mutateAsyncDisableCategories(dialogIds).then(() => {
      refetch();
      onDisableModalClose();
      setDialogIds({ ids: [] });
    });
  };

  // delete category handler
  const handleSingleDelete = (id: number) => {
    setDialogIds({ ids: [id] });
    onDeleteModalOpen();
  };

  const handleBulkDelete = (selectRows: any) => {
    const ids = selectRows.map((row: any) => row.original.id);
    setDialogIds({ ids: ids });
    onDeleteModalOpen();
  };

  const deleteCategory = () => {
    mutateDeleteAsync(dialogIds).then(() => {
      refetch();
      onDeleteModalClose();
      setDialogIds({ ids: [] });
    });
  };

  const handleCategoryStatusChange = (selectedOption: ILabelValueOptions) => {
    setSearchQuery({ ...searchQuery, page: 1, active: selectedOption?.value === 'active' ? '1' : '0' });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handlePageSizeChange = (pageSize: number) => {
    setSearchQuery(queries => ({ ...queries, page_size: pageSize, page: 1 }));
  };

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: ICategorySearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  return (
    <>
      <ControllableDrawer onClose={handleDrawerClose} isOpen={isDrawerOpen} size="lg">
        <DrawerContent>
          <DrawerCloseButton />
          <AddCategory onClose={handleDrawerClose} formData={formData} />
        </DrawerContent>
      </ControllableDrawer>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        title={`${t('timeOff:deleteCategoryModalTitle')}`}
        subTitle={t('timeOff:deleteCategoryModalSubtitle')}
        confirmButtonLabel={t('common:delete')}
        modalProps={{ size: '3xl' }}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: deleteStatus === 'loading',
          onClick: deleteCategory,
        }}
      />

      <ConfirmationModal
        isOpen={isEnableModalOpen}
        onClose={onEnableModalClose}
        title={`${t('timeOff:enableCategoryModalTitle')}`}
        subTitle={t('timeOff:enableCategoryModalSubtitle')}
        confirmButtonLabel={t('common:activate')}
        modalProps={{ size: '3xl' }}
        confirmButtonProps={{
          bg: 'success.200',
          color: 'success.500',
          _hover: {
            bg: 'success.600',
            color: 'white',
          },
          isLoading: enableCategoriesStatus === 'loading',
          onClick: enableCategories,
        }}
      />

      <ConfirmationModal
        isOpen={isDisableModalOpen}
        onClose={onDisableModalClose}
        title={`${t('timeOff:disableCategoryModalTitle')}`}
        subTitle={t('timeOff:disableCategoryModalSubtitle')}
        confirmButtonLabel={t('common:deactivate')}
        modalProps={{ size: '3xl' }}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: disableCategoriesStatus === 'loading',
          onClick: disableCategories,
        }}
      />

      <Flex
        flexDir={{ base: 'column', sm: 'row' }}
        alignItems="center"
        mb="5"
        gridGap="2"
        justifyContent="space-between"
      >
        <InputGroup maxWidth={{ base: '100%', sm: '180px' }} height="fit-content">
          <InputLeftAddon children="Status" px="2" />
          <ReactSelect
            options={statusOptions}
            size="md"
            fontSize="sm"
            isSearchable
            defaultValue={{ label: 'Active', value: 'active' }}
            placeholder="Select Status"
            onChange={handleCategoryStatusChange}
          />
        </InputGroup>
        <Box maxWidth={{ md: '350px' }} width="100%">
          <SearchField
            inputProps={{
              placeholder: t('timeOff:searchCategory'),
              onChange: handleSearchInputChange,
            }}
          />
        </Box>
      </Flex>
      <Table
        columns={columns}
        data={categoryListData?.payload || []}
        paginationData={categoryListData?.meta_data?.pagination}
        isLoading={categoryListStatus === 'loading'}
        enableRowSelect
        handlePageSizeChange={handlePageSizeChange}
        onPageChange={handlePageChange}
        paginationStyle="rowsPerPage"
        emptyTableStateProps={{
          searchText: searchQuery.search,
        }}
        renderTableFooter={(selectedFlatRows: any, toggleAllRowSelected) => {
          return (
            <CategoriesTableFooter
              selectedFlatRows={selectedFlatRows}
              toggleAllRowSelected={toggleAllRowSelected}
              handleBulkDelete={handleBulkDelete}
              handleBulkEnable={handleBulkEnable}
              handleBulkDisable={handleBulkDisable}
            />
          );
        }}
      />
    </>
  );
};
export default Categories;
